import { palette } from '@app/palette';
import { ArrowRightAltSharp, KeyboardArrowDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  styled,
  Typography,
} from '@mui/material';
import React from 'react';
import { AccountabilityPartnerDropdown } from '@app/actions/Admin/Group/AccountabilityDropdown';
import { DebugPanel } from '@app/components';
import { useAccountabilityPartnerPairingEditorController } from '@components/Admin/AccountabilityPartnerPairingEditor/UseAccountabilityPartnerPairingEditorController';

interface AccountabilityPartnerPairingEditorProps {
  groupId: number;
  open: boolean;
  closeDialog: () => void;
}

export const AccountabilityPartnerPairingEditor: React.FC<AccountabilityPartnerPairingEditorProps> = ({
  groupId,
  open,
  closeDialog,
}) => {
  const {
    data,
    isLoading,
    savePartnerships,
    selectedPartners,
    setSelectedPartners,
    clearPartners,
    clearAllPartnerships,
  } = useAccountabilityPartnerPairingEditorController(groupId);

  return (
    <Dialog open={open} fullWidth={true} maxWidth={'lg'} onClose={closeDialog} aria-labelledby="form-dialog-title">
      <DebugPanel displayItem={selectedPartners}></DebugPanel>
      {isLoading && <CircularProgress variant={'indeterminate'}></CircularProgress>}
      <DialogTitle>Accountability Partner Pairing Editor</DialogTitle>
      <DialogContent>
        <Box pb={2}>
          <Accordion variant={'elevation'}>
            <AccordionSummary expandIcon={<KeyboardArrowDown />}>More Info</AccordionSummary>
            <AccordionDetails>
              <Typography>
                When setting accountability partners make sure of the following:
                <ol>
                  <li>Each person has an accountability partner.</li>
                  <li>Each person has a different accountability partner.</li>
                  <li>
                    Each person&apos;s accountability partner matches the other, so if Ben&apos;s AP is Mary then
                    Mary&apos;s AP is Ben.
                  </li>
                </ol>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
        <Grid container pb={2} direction={'row'} wrap={'nowrap'} justifyContent={'space-around'} spacing={4}>
          <Grid size={12}>Participant</Grid>
          <Grid size={1}></Grid>
          <Grid size={12}>Accountability Partner</Grid>
        </Grid>
        {Array.from(data.entries()).map(([participant, partnerOptions], idx) => {
          const isLastParticipant = idx === data.size - 1;
          const shouldRenderTriad = data.size % 2 !== 0 && isLastParticipant;
          return (
            <Grid
              container
              pb={2}
              direction={'row'}
              wrap={'nowrap'}
              justifyContent={'space-around'}
              spacing={4}
              key={participant.id}
            >
              <Grid size={12}>
                <Item>{participant.name}</Item>
              </Grid>
              <Grid size={1} alignContent={'center'}>
                <ArrowRightAltSharp amplitude={2} />
              </Grid>
              <Grid size={12}>
                <AccountabilityPartnerDropdown
                  setSelectedPartners={setSelectedPartners}
                  selectedPartners={selectedPartners}
                  clearPartner={clearPartners}
                  participantId={participant.id}
                  allPartners={partnerOptions}
                ></AccountabilityPartnerDropdown>
              </Grid>
              {shouldRenderTriad && (
                <Grid size={12}>
                  <AccountabilityPartnerDropdown
                    setSelectedPartners={setSelectedPartners}
                    selectedPartners={selectedPartners}
                    clearPartner={clearPartners}
                    participantId={participant.id}
                    allPartners={partnerOptions}
                  ></AccountabilityPartnerDropdown>
                </Grid>
              )}
            </Grid>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={clearAllPartnerships}>Clear All Partners</Button>
        <Button variant={'text'}>Cancel</Button>
        <Button
          variant={'contained'}
          size={'large'}
          onClick={async () => {
            savePartnerships();
            closeDialog();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Item: React.FC<React.PropsWithChildren> = ({ children }) => {
  const ParticipantItem = styled(Paper)(({ theme }) => ({
    backgroundColor: palette.background.paper,
    ...theme.typography.body1,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: palette.primary.main,
  }));

  return <ParticipantItem>{children}</ParticipantItem>;
};
